/** A preview draws at most this many data rows and columns; Download has the rest. */
export const MAX_CSV_ROWS = 1000;
export const MAX_CSV_COLS = 100;

/**
 * A parsed CSV as a table (#335): the first row is the sticky header, short
 * rows are padded to the widest one, and cells are plain text. Both rows and
 * columns are capped, so one very wide row cannot make every row huge.
 */
export function CsvTable({ rows }: { rows: string[][] }) {
  const [head, ...body] = rows;
  const shown = body.slice(0, MAX_CSV_ROWS);
  // A loop, not Math.max(...): spreading a huge array overflows the stack.
  let widest = 0;
  for (const r of rows) if (r.length > widest) widest = r.length;
  const width = Math.min(widest, MAX_CSV_COLS);
  const pad = (r: string[]) => {
    const cut = r.slice(0, width);
    return cut.length < width ? [...cut, ...Array<string>(width - cut.length).fill('')] : cut;
  };

  return (
    <div className="space-y-2">
      {(body.length > MAX_CSV_ROWS || widest > MAX_CSV_COLS) && (
        <p className="m-0 text-xs text-text-muted">
          {body.length > MAX_CSV_ROWS && `Showing the first ${MAX_CSV_ROWS.toLocaleString()} of ${body.length.toLocaleString()} rows. `}
          {widest > MAX_CSV_COLS && `Showing the first ${MAX_CSV_COLS.toLocaleString()} of ${widest.toLocaleString()} columns. `}
          Download it for the rest.
        </p>
      )}
      <div className="max-h-[70vh] overflow-auto rounded-md border border-border">
        <table className="w-full border-collapse text-xs text-text-primary">
          <thead className="sticky top-0 bg-bg-hover">
            <tr>
              {pad(head).map((c, i) => (
                <th key={i} className="border-b border-border px-2.5 py-1.5 text-left font-semibold whitespace-nowrap">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, ri) => (
              <tr key={ri} className="odd:bg-bg-surface even:bg-bg-card">
                {pad(r).map((c, ci) => (
                  <td key={ci} className="border-b border-border/50 px-2.5 py-1 align-top whitespace-pre-wrap break-words max-w-[24rem]">{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
